import React, {useEffect, useState} from 'react';

import {useStore} from 'effector-react';
import {saveAs} from 'file-saver';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {exportWithdrawalAuditEvent, fetchWithdrawalAuditEvent} from 'api/withdrawalAudit';
import {ReactComponent as DownloadIcon} from 'assets/svg/download.svg';
import {AuditGeneralInformation} from 'components/common/AuditLog/GeneralInformation';
import {AuditMarkingCodesTable} from 'components/common/AuditLog/MarkingCodesTable';
import {AuditReference} from 'components/common/AuditLog/Reference';
import {
    CountRow,
    CountsTable,
    DetailCard,
    DetailHeader,
    DetailHeaderActions,
    DetailId,
    DetailTitle,
    EmptyDetail,
    EventBadge,
    Field,
    FieldLabel,
    FieldsGrid,
    FieldValue,
    Section,
    SectionTitle,
} from 'components/common/AuditLog/styled';
import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {formatLocalDateTime} from 'components/pages/RecallAudit/utils';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {Spinner} from 'components/ui-kit/Spinner';
import {PERMISSION_ALIASES} from 'core/constants/permissionAliases';
import {PRODUCT_STATUS_COLORS} from 'core/constants/statuses';
import {ProductStatus} from 'core/dictionaries';
import {RestError, showRestError} from 'core/errors';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {WithdrawalAuditEntry, WithdrawalAuditEventType} from 'types/withdrawal-audit';

type DetailField = Exclude<keyof WithdrawalAuditEntry, 'previousStatuses' | 'failedMarkingCodes'>;

const eventFields: Record<WithdrawalAuditEventType, DetailField[]> = {
    WITHDRAWAL_DOCUMENT_CREATED: [
        'documentId',
        'withdrawalReason',
        'supportingDocumentNumber',
        'supportingDocumentDate',
        'affectedCodesCount',
        'comment',
    ],
    WITHDRAWAL_DOCUMENT_AUTO_CREATED: [
        'documentId',
        'withdrawalReason',
        'sourceFormation',
        'affectedCodesCount',
    ],
    WITHDRAWAL_DOCUMENT_COMPLETED: ['documentId', 'withdrawalReason', 'affectedCodesCount'],
    WITHDRAWAL_DOCUMENT_REJECTED: [
        'documentId',
        'withdrawalReason',
        'rejectionReason',
        'errorCode',
    ],
};
const optionalFields: DetailField[] = [
    'supportingDocumentNumber',
    'supportingDocumentDate',
    'comment',
    'errorCode',
];

export const WithdrawalAuditDetailPage = () => {
    const {id} = useParams<{id: string}>();
    const {t} = useTranslation();
    const permissions = useStore(permissions$);
    const [entry, setEntry] = useState<WithdrawalAuditEntry | null>(null);
    const [pending, setPending] = useState(true);
    const [exportPending, setExportPending] = useState(false);
    useEffect(() => {
        let active = true;
        setEntry(null);
        setPending(true);
        fetchWithdrawalAuditEvent(id)
            .then((value) => {
                if (active) setEntry(value);
            })
            .catch((error) => {
                if (active) showRestError(error as RestError);
            })
            .finally(() => {
                if (active) setPending(false);
            });
        return () => {
            active = false;
        };
    }, [id]);
    const exportPdf = async () => {
        if (exportPending) return;
        setExportPending(true);
        try {
            saveAs(await exportWithdrawalAuditEvent(id), `withdrawal-audit-${id}.pdf`);
        } catch (error) {
            showRestError(error as RestError);
        } finally {
            setExportPending(false);
        }
    };
    const renderValue = (key: DetailField): React.ReactNode => {
        const value = entry?.[key];
        if (value == null || value === '') return '—';
        if (key === 'withdrawalReason')
            return t(`dictionaries.documents.DropOut.reasons.${value}`, {
                defaultValue: String(value),
            });
        if (key === 'sourceFormation')
            return t(`pages.withdrawalAudit.sources.${value}`, {defaultValue: String(value)});
        if (key === 'supportingDocumentDate') return formatLocalDateTime(String(value));
        if (['documentId', 'markingCode', 'oldMarkingCode', 'newMarkingCode'].includes(key))
            return <AuditReference field={key} value={value} />;
        return value;
    };
    return (
        <Page title={t('pages.recallAudit.detail.title')}>
            <BackTo to={ROUTE.WITHDRAWAL_AUDIT}>{t('pages.recallAudit.detail.back')}</BackTo>
            {pending ? (
                <EmptyDetail>
                    <Spinner />
                </EmptyDetail>
            ) : !entry ? (
                <EmptyDetail>{t('pages.recallAudit.detail.notFound')}</EmptyDetail>
            ) : (
                <DetailCard>
                    <DetailHeader>
                        <div>
                            <DetailTitle>{t('pages.recallAudit.detail.title')}</DetailTitle>
                            <DetailId>ID: {entry.eventId}</DetailId>
                        </div>
                        <DetailHeaderActions>
                            <EventBadge>
                                {t(`pages.withdrawalAudit.events.${entry.eventType}`, {
                                    defaultValue: entry.eventType,
                                })}
                            </EventBadge>
                            {permissions.includes(PERMISSION_ALIASES.withdrawalAuditLogExport) && (
                                <Button
                                    size="sm"
                                    variant={ButtonVariant.Secondary}
                                    disabled={exportPending}
                                    isLoading={exportPending}
                                    onClick={exportPdf}
                                    icon={<Icon source={DownloadIcon} width={16} height={16} />}
                                >
                                    {t('pages.recallAudit.export.pdf')}
                                </Button>
                            )}
                        </DetailHeaderActions>
                    </DetailHeader>
                    <AuditGeneralInformation entry={entry} />
                    <Section>
                        <SectionTitle>{t('pages.withdrawalAudit.details')}</SectionTitle>
                        <FieldsGrid>
                            {(eventFields[entry.eventType] || [])
                                .filter(
                                    (key) =>
                                        !optionalFields.includes(key) ||
                                        (entry[key] != null && entry[key] !== ''),
                                )
                                .map((key) => (
                                    <Field key={key} wide={key === 'comment'}>
                                        <FieldLabel>
                                            {t(`pages.withdrawalAudit.fields.${key}`)}
                                        </FieldLabel>
                                        <FieldValue>{renderValue(key)}</FieldValue>
                                    </Field>
                                ))}
                        </FieldsGrid>
                    </Section>
                    {entry.eventType === 'WITHDRAWAL_DOCUMENT_COMPLETED' &&
                        !!entry.previousStatuses?.length && (
                            <Section>
                                <SectionTitle>
                                    {t('pages.withdrawalAudit.previousStatuses')}
                                </SectionTitle>
                                <CountsTable>
                                    <CountRow header>
                                        <span>
                                            {t('pages.withdrawalAudit.fields.previousStatus')}
                                        </span>
                                        <span>{t('pages.withdrawalAudit.fields.codesCount')}</span>
                                    </CountRow>
                                    {entry.previousStatuses.map(({status, count}, index) => (
                                        <CountRow key={`${status}-${index}`}>
                                            <span>
                                                <StatusWrapper
                                                    color={
                                                        PRODUCT_STATUS_COLORS[
                                                            status as ProductStatus
                                                        ] || PRODUCT_STATUS_COLORS.DEFAULT
                                                    }
                                                >
                                                    {t(`dictionaries.products.status.${status}`, {
                                                        defaultValue: status,
                                                    })}
                                                </StatusWrapper>
                                            </span>
                                            <span>{count}</span>
                                        </CountRow>
                                    ))}
                                </CountsTable>
                            </Section>
                        )}
                    {entry.eventType === 'WITHDRAWAL_DOCUMENT_REJECTED' &&
                        !!entry.failedMarkingCodes?.length && (
                            <Section>
                                <SectionTitle>
                                    {t('pages.withdrawalAudit.failedMarkingCodes')}
                                </SectionTitle>
                                <AuditMarkingCodesTable
                                    key={entry.eventId}
                                    codes={entry.failedMarkingCodes}
                                    label={t('pages.withdrawalAudit.fields.markingCode')}
                                />
                            </Section>
                        )}
                </DetailCard>
            )}
        </Page>
    );
};
